import { CyclesInterface } from 'types/user';
import { EquipeResumo, MembroResumo, RealizacaoResumo } from 'services/minha-equipe.service';

/**
 * Regras puras da página Minha Equipe (spec `minha-equipe-nova-identidade`): status da realização na
 * fase, fase de referência, rótulos, totais do panorama e a linha da lista. Os componentes só desenham.
 */

export type StatusDaFase = 'aguardando' | 'devolvida' | 'validada' | 'sem';

/** Janela de cadastramento do mês de referência (milissegundos, limites inclusive). */
export interface JanelaDaFase {
  inicio: number;
  fim: number;
  mes: string;
}

/** Funcionário como o `find/equipe_gestor` devolve (`matricula` é a chave, `_id` o id do player_fields). */
export interface FunciDaEquipe {
  _id: string;
  matricula: string;
  nome: string;
  nome_guerra?: string;
  diretoria?: string;
  funcao?: string;
  /** O `equipe_gestor` projeta `avatar` e `avatar_type` trocados; ver `rostoDoFunci`. */
  avatar?: unknown;
  avatar_type?: unknown;
  image?: { small?: { url?: string }; medium?: { url?: string } } | null;
}

/** Rosto da pessoa no formato do `PersonAvatar` (foto, avatar montado e a escolha entre os dois). */
export interface RostoDoFunci {
  image?: string;
  avatar?: Record<string, unknown>;
  avatarType?: number;
}

export interface LinhaDaEquipe {
  id: string;
  chave: string;
  nome: string;
  nomeGuerra?: string;
  diretoria: string;
  funcao: string;
  /** null = resumo indisponível (a tela mostra "—"). */
  pontos: number | null;
  /** Realizações cadastradas pelo liderado no ciclo vigente (MEQ2-02). */
  realizacoes: number | null;
  selos: number | null;
  reconhecimentos: number | null;
  status: StatusDaFase | null;
  acordo?: string;
  rosto: RostoDoFunci;
}

const STATUS_VALIDADOS = [
  'aprovado',
  'dobrado',
  'promovido',
  'concluido',
  'concluidoAuto',
  'apreciacaoExpirada',
  'valeu',
  'mandou',
  'selo',
];

const PRIORIDADE: StatusDaFase[] = ['aguardando', 'devolvida', 'validada'];

const ROTULOS_DO_STATUS: Record<StatusDaFase, { rotulo: string; cor: string }> = {
  aguardando: { rotulo: 'Realização em validação', cor: 'bg-brand-pink-light' },
  validada: { rotulo: 'Realização validada', cor: 'bg-brand-green' },
  devolvida: { rotulo: 'Realização devolvida', cor: 'bg-brand-yellow' },
  sem: { rotulo: 'Sem realização na fase', cor: 'bg-surface-200' },
};

const ROTULOS_DO_ACORDO: Record<string, string> = {
  HIBRIDO: 'Híbrido',
  PREDEFINIDO: 'Predefinido',
  'SEM ACORDO': 'Sem acordo',
};

/** Status de uma realização; null para status fora da régua (não entra na prioridade). */
export function statusDaRealizacao(status: string): StatusDaFase | null {
  if (status === 'aguardando') return 'aguardando';
  if (status === 'rejeitado') return 'devolvida';
  if (STATUS_VALIDADOS.includes(status)) return 'validada';
  return null;
}

/** Status do liderado na fase: aguardando > devolvida > validada > sem realização. */
export function statusDaFase(realizacoes: RealizacaoResumo[], janela: JanelaDaFase | null): StatusDaFase {
  if (!janela) return 'sem';

  const naFase = realizacoes
    .filter(realizacao => realizacao.created >= janela.inicio && realizacao.created <= janela.fim)
    .map(realizacao => statusDaRealizacao(realizacao.status));

  return PRIORIDADE.find(status => naFase.includes(status)) ?? 'sem';
}

export function rotuloDoStatus(status: StatusDaFase) {
  return ROTULOS_DO_STATUS[status];
}

/** "Nível 1 - Setembro" → "Setembro" (meses antigos já vêm só com o nome). */
export function nomeDoMes(mes: CyclesInterface): string {
  return mes.title.replace(/^Nível \d+ - /, '');
}

function janelaDoMes(mes: CyclesInterface): JanelaDaFase {
  return { inicio: mes.date_start, fim: mes.date_finish, mes: nomeDoMes(mes) };
}

/** Mês em Validação ou N1 (`evaluation`); fora dessa janela, o último mês com cadastramento fechado. */
export function faseDeReferencia(cycles: CyclesInterface[] | undefined, agora: number): JanelaDaFase | null {
  if (!cycles?.length) return null;

  const emAvaliacao = cycles.find(mes => mes.status === 'evaluation');
  if (emAvaliacao) return janelaDoMes(emAvaliacao);

  const fechados = cycles.filter(mes => mes.date_finish < agora).sort((a, b) => b.date_finish - a.date_finish);
  return fechados.length ? janelaDoMes(fechados[0]) : null;
}

/** Mês cujo cadastramento contém a data de cadastro da realização. */
export function faseDoCadastro(created: number, cycles: CyclesInterface[] | undefined): string | null {
  const mes = cycles?.find(item => created >= item.date_start && created <= item.date_finish);
  return mes ? nomeDoMes(mes) : null;
}

/** Rótulo da pílula de acordo de trabalho; null esconde a pílula. */
export function rotuloDoAcordo(tipo: string | undefined): string | null {
  return (tipo && ROTULOS_DO_ACORDO[tipo]) || null;
}

export function numeroPtBR(valor: number | null): string {
  return valor === null ? '—' : valor.toLocaleString('pt-BR');
}

function totalDeReconhecimentos(membro: MembroResumo): number {
  return membro.reconhecimentos.valeu + membro.reconhecimentos.mandou + membro.reconhecimentos.uau;
}

export function totaisDoPanorama(membros: MembroResumo[] | null) {
  if (!membros) return { realizacoes: null, selos: null, reconhecimentos: null };

  // MEQ2-01: o painel soma as realizações cadastradas pela equipe no ciclo, e não mais os pontos.
  return membros.reduce(
    (totais, membro) => ({
      realizacoes: totais.realizacoes + membro.realizacoes.length,
      selos: totais.selos + membro.selos,
      reconhecimentos: totais.reconhecimentos + totalDeReconhecimentos(membro),
    }),
    { realizacoes: 0, selos: 0, reconhecimentos: 0 },
  );
}

/**
 * Rosto do liderado, com a mesma regra da Home (`PersonAvatar`). O `equipe_gestor` devolve o avatar
 * montado em `avatar_type` e a escolha (0 = avatar, 1 = foto) em `avatar` — trocados no `$addFields`
 * do aggregate. Cada campo é reconhecido pelo tipo do valor, não pelo nome, para continuar certo se o
 * aggregate for corrigido.
 */
export function rostoDoFunci(funci: Partial<FunciDaEquipe>): RostoDoFunci {
  const campos = [funci.avatar, funci.avatar_type];
  const avatar = campos.find((valor): valor is Record<string, unknown> => !!valor && typeof valor === 'object');
  const avatarType = campos.find((valor): valor is number => typeof valor === 'number');
  const image = funci.image?.small?.url || funci.image?.medium?.url || undefined;

  return {
    ...(image && { image }),
    ...(avatar && { avatar }),
    ...(avatarType !== undefined && { avatarType }),
  };
}

/** Pessoa no formato do `player`, como as buscas de pessoas a devolvem (`image`, `extra.avatar`, `extra.avatar_type`). */
export interface PlayerComRosto {
  image?: FunciDaEquipe['image'];
  extra?: { avatar?: unknown; avatar_type?: unknown };
}

/** Rosto de quem a busca devolveu, com a mesma regra da Home (avatar montado, foto ou iniciais). */
export function rostoDoPlayer(player: PlayerComRosto): RostoDoFunci {
  return rostoDoFunci({ image: player.image, avatar: player.extra?.avatar, avatar_type: player.extra?.avatar_type });
}

/**
 * Números do liderado no resumo. Liderado que não veio no resumo tem zero em tudo e nenhuma realização
 * (edge case "resumo não traz um liderado"); `null` só quando o próprio resumo está indisponível.
 */
export function membroDoResumo(resumo: EquipeResumo | null, chave: string): MembroResumo | null {
  if (!resumo) return null;
  return (
    resumo.membros.find(membro => membro.chave === chave) ?? {
      chave,
      pontos: 0,
      selos: 0,
      reconhecimentos: { valeu: 0, mandou: 0, uau: 0 },
      realizacoes: [],
    }
  );
}

/** Linha da lista: funcionário do equipe_gestor + números do resumo (ausente no resumo = zeros). */
export function linhaDaEquipe(funci: FunciDaEquipe, resumo: EquipeResumo | null, janela: JanelaDaFase | null): LinhaDaEquipe {
  const linha = {
    id: funci._id,
    chave: funci.matricula,
    nome: funci.nome,
    nomeGuerra: funci.nome_guerra,
    diretoria: funci.diretoria ?? '',
    funcao: funci.funcao ?? '',
    rosto: rostoDoFunci(funci),
  };

  const membro = membroDoResumo(resumo, funci.matricula);
  if (!membro) return { ...linha, pontos: null, realizacoes: null, selos: null, reconhecimentos: null, status: null };

  return {
    ...linha,
    pontos: membro.pontos,
    realizacoes: membro.realizacoes.length,
    selos: membro.selos,
    reconhecimentos: totalDeReconhecimentos(membro),
    status: statusDaFase(membro.realizacoes, janela),
    ...(membro.tipo_de_acordo !== undefined && { acordo: membro.tipo_de_acordo }),
  };
}
